// frontend/src/components/Inventory/InventorySelector.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash, FaBroom, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import api from "../../api";

export default function InventorySelector({
  selectedItems = [],
  setSelectedItems,
  catalogue: catalogueProp,
  itemsPerPage = 12,
  showSelectedList = true,
  showClearButton = true,
}) {
  const { t } = useTranslation();
  const [catalogue, setCatalogue] = useState(Array.isArray(catalogueProp) ? catalogueProp : []);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const API_BASE = (api?.defaults?.baseURL || "").replace(/\/$/, "");
  const resolveImgUrl = (u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/")) return `${API_BASE}${u}`;
    return `${API_BASE}/${u}`;
  };

  // Charger le catalogue si non fourni
  useEffect(() => {
    if (Array.isArray(catalogueProp)) {
      setCatalogue(catalogueProp);
      return;
    }
    let mounted = true;
    api
      .get("/inventory")
      .then((res) => {
        if (!mounted) return;
        setCatalogue(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!mounted) return;
        setCatalogue([]);
      });
    return () => {
      mounted = false;
    };
  }, [catalogueProp]);

  // Ajout / remove / clear / update qty
  const addItem = (item) => {
    if (!setSelectedItems) return;
    const exists = selectedItems.find((i) => i.id === item.id);
    if (exists) {
      setSelectedItems(
        selectedItems.map((i) =>
          i.id === item.id ? { ...i, quantity: (Number(i.quantity) || 0) + 1 } : i
        )
      );
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          id: item.id,
          name: item.name,
          volume: Number(item.volume) || 0,
          quantity: 1,
          image_url: item.image_url || null,
        },
      ]);
    }
  };

  const removeItem = (id) => {
    if (!setSelectedItems) return;
    setSelectedItems(selectedItems.filter((i) => i.id !== id));
  };

  const clearInventory = () => {
    if (!setSelectedItems) return;
    setSelectedItems([]);
  };

  const updateQty = (id, qty) => {
    if (!setSelectedItems) return;
    const q = Math.max(1, Number(qty) || 1);
    setSelectedItems(selectedItems.map((i) => (i.id === id ? { ...i, quantity: q } : i)));
  };

  // Recherche + pagination
  const filtered = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    if (!s) return catalogue;
    return catalogue.filter((c) => (c.name || "").toLowerCase().includes(s));
  }, [catalogue, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const start = (page - 1) * itemsPerPage;
  const visible = filtered.slice(start, start + itemsPerPage);

  // Pages compactes: 1 … p-1 p p+1 … N
  const getPageNumbers = () => {
    const pages = [];
    const add = (p) => pages.push(p);
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) add(i);
      return pages;
    }
    add(1);
    if (page > 3) add("…");
    const before = Math.max(2, page - 1);
    const after = Math.min(totalPages - 1, page + 1);
    for (let i = before; i <= after; i++) add(i);
    if (page < totalPages - 2) add("…");
    add(totalPages);
    return pages;
  };

  // Volume total sélectionné
  const totalVolume = useMemo(
    () =>
      (selectedItems || []).reduce(
        (sum, it) => sum + (Number(it.volume) || 0) * (Number(it.quantity) || 0),
        0
      ),
    [selectedItems]
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center flex-wrap gap-2">
          <h3 className="text-base font-bold text-base-content">📦 {t("quote.inventory", "Inventaire")}</h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-base-200 text-base-content/60">
            {(selectedItems || []).length} {t("quote.items", "objets")}
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary">
            {totalVolume.toFixed(2)} m³
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t("quote.search", "Rechercher un objet…")}
            className="rounded-full px-4 py-2 w-56 text-sm bg-base-200 border border-base-300 text-base-content placeholder:text-base-content/40 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
          />
          {!!(selectedItems || []).length && showClearButton && (
            <button
              type="button"
              onClick={clearInventory}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-base-content/50 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
              title={t("quote.clearInventory", "Vider l'inventaire")}
            >
              <FaBroom size={13} /> {t("quote.clear", "Vider")}
            </button>
          )}
        </div>
      </div>

      {/* Catalogue (page courante) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map((it) => {
          const count = (selectedItems || []).find((i) => i.id === it.id)?.quantity || 0;
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => addItem(it)}
              className={`group relative flex flex-col rounded-xl border bg-base-100 p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] ${
                count > 0 ? "border-primary/50 ring-1 ring-primary/20" : "border-base-200 hover:border-primary/30"
              }`}
              title={t("quote.add", "Ajouter")}
            >
              {count > 0 && (
                <span className="absolute top-2 right-2 z-10 min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-content text-[11px] font-bold flex items-center justify-center shadow">
                  {count}
                </span>
              )}
              {it.image_url ? (
                <img
                  src={resolveImgUrl(it.image_url)}
                  alt={it.name}
                  className="h-24 w-full object-contain mb-2"
                />
              ) : (
                <div className="h-24 w-full flex items-center justify-center rounded-lg bg-base-200 text-base-content/40 text-xs mb-2">
                  {t("quote.noImage", "Pas d'image")}
                </div>
              )}
              <div className="font-semibold text-sm text-base-content truncate">{it.name}</div>
              <div className="text-xs text-base-content/50 mt-0.5">{it.volume} m³</div>
            </button>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full text-sm text-base-content/40 italic py-8 text-center">
            {t("quote.noResults", "Aucun résultat")}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 select-none">
          <button
            type="button"
            className="w-8 h-8 rounded-lg border border-base-300 bg-base-100 text-base-content/70 hover:bg-base-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <FaChevronLeft className="inline-block" size={11} />
          </button>
          {getPageNumbers().map((p, idx) =>
            p === "…" ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-base-content/30">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg border text-xs font-semibold transition-colors ${
                  page === p
                    ? "bg-primary text-primary-content border-primary"
                    : "bg-base-100 border-base-300 text-base-content/70 hover:bg-base-200"
                }`}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="w-8 h-8 rounded-lg border border-base-300 bg-base-100 text-base-content/70 hover:bg-base-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <FaChevronRight className="inline-block" size={11} />
          </button>
        </div>
      )}

      {/* Liste sélectionnée (affichage conditionnel) */}
      {showSelectedList && (selectedItems || []).length > 0 ? (
        <div className="bg-base-200/40 border border-base-200 rounded-xl p-4">
          <h4 className="font-semibold text-sm text-base-content/70 mb-3">{t("quote.selectedItems", "Éléments sélectionnés")}</h4>
          <div className="space-y-2">
            {selectedItems.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 bg-base-100 border border-base-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  {it.image_url ? (
                    <img
                      src={resolveImgUrl(it.image_url)}
                      alt={it.name}
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-base-200 rounded flex items-center justify-center text-xs text-base-content/40">
                      —
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-base-content truncate">{it.name}</div>
                    <div className="text-xs text-base-content/50">
                      {(Number(it.volume) || 0).toFixed(2)} m³ / u
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={Number(it.quantity) || 1}
                    onChange={(e) => updateQty(it.id, e.target.value)}
                    className="w-16 px-2 py-1 rounded-lg text-center text-sm bg-base-200 border border-base-300 text-base-content focus:outline-none focus:border-primary"
                  />
                  <span className="text-xs font-semibold text-primary w-16 text-right">
                    {((Number(it.volume) || 0) * (Number(it.quantity) || 0)).toFixed(2)} m³
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                    title={t("common.delete", "Supprimer")}
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-base-200 text-right text-sm font-bold text-primary">
            {t("quote.totalVolume", "Volume total")} : {totalVolume.toFixed(2)} m³
          </div>
        </div>
      ) : null}
    </div>
  );
}
